import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  EmptyState,
  StatusPill,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  type Tone,
} from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";
import type {
  ExtractionJobDTO,
  ExtractionJobStatus,
} from "@wmart/shared-types";

const STATUS_TONE: Record<ExtractionJobStatus, Tone> = {
  queued: "neutral",
  extracting: "info",
  ready: "success",
  failed: "danger",
  completed: "olive",
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function truncateRef(ref: string, max = 40): string {
  if (ref.length <= max) return ref;
  // Keep the head (scheme/host for URLs, prefix for keys) — the tail is the
  // least identifying part for a supplier URL or GCS key.
  return `${ref.slice(0, max - 1)}…`;
}

export function ExtractionsList() {
  const { notify, notifyProblem } = useToast();
  const [items, setItems] = useState<ExtractionJobDTO[]>([]);
  const [loading, setLoading] = useState(false);

  async function fetchJobs() {
    setLoading(true);
    try {
      const data = await api.get<ExtractionJobDTO[]>(
        "/v1/admin/catalog/extractions",
      );
      setItems(data);
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Failed to load extraction jobs");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-display-sm text-ink-900">
          Supplier Extractions
        </h1>
        <Link to="/catalog/extractions/new">
          <Button>New extraction</Button>
        </Link>
      </div>

      {/* Content */}
      {items.length === 0 && !loading ? (
        <EmptyState
          icon="fileText"
          title="No extraction jobs"
          body="Upload a supplier PDF or paste a URL to extract products."
        />
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>Date</TH>
              <TH>Source</TH>
              <TH>Status</TH>
              <TH>Items</TH>
            </TR>
          </THead>
          <TBody>
            {items.map((job) => (
              <TR key={job.id}>
                <TD className="text-body-sm text-ink-500">
                  {formatDate(job.created_at)}
                </TD>
                <TD className="text-body-sm">
                  <span className="inline-block rounded bg-ink-100 px-1.5 py-0.5 text-caption font-medium text-ink-600 mr-2">
                    {job.source_type}
                  </span>
                  <span className="font-mono text-ink-700">
                    {truncateRef(job.source_ref)}
                  </span>
                </TD>
                <TD>
                  <Link
                    to={`/catalog/extractions/${job.id}`}
                    className="hover:underline"
                    aria-label={job.status}
                  >
                    <StatusPill tone={STATUS_TONE[job.status]}>
                      {job.status}
                    </StatusPill>
                  </Link>
                </TD>
                <TD className="text-body-sm text-ink-700">{job.item_count}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </div>
  );
}
